/**
 * MethodologyPage — explains the math behind every dashboard metric.
 *
 * Content covers: HF, E-Mode, CaR, LaR, Concentration, Utilization,
 * Slippage Curves, Historical Tracking, Validation, Limitations, Pipeline.
 *
 * Pure component, no data dependencies.
 */

interface MethodologyPageProps {
  onBack: () => void;
}

export function MethodologyPage({ onBack }: MethodologyPageProps) {
  return (
    <main className="max-w-4xl mx-auto px-3 sm:px-6 py-8 space-y-10">
      <button
        onClick={onBack}
        className="text-accent text-sm hover:underline flex items-center gap-1"
      >
        &larr; Back to Dashboard
      </button>

      <div>
        <h1 className="text-2xl font-bold tracking-tight">Methodology</h1>
        <p className="text-text-secondary mt-2 text-sm leading-relaxed">
          This page documents the mathematical framework behind every metric on the dashboard.
          All computations run against ~25,000 live positions from Aave V3 and Compound V3 on Ethereum mainnet,
          refreshed every 15 minutes. Liquidity depth data is refreshed every 3 hours via DEX aggregator quotes.
        </p>
      </div>

      <Section id="health-factor" title="Health Factor">
        <P>
          Health Factor (HF) answers: <em>&ldquo;How close is this position to liquidation right now?&rdquo;</em>
        </P>
        <Formula>{"HF = \u03a3(C\u1d62 \u00d7 P\u1d62 \u00d7 LT\u1d62) / \u03a3(D\u2c7c \u00d7 P\u2c7c)"}</Formula>
        <Dl
          items={[
            ["C\u1d62", "Amount of collateral asset i (token units)"],
            ["P\u1d62", "Current USD price of collateral asset i"],
            ["LT\u1d62", "Liquidation threshold for asset i (protocol parameter, e.g. 0.83 for WETH)"],
            ["D\u2c7c", "Amount of debt asset j (token units)"],
            ["P\u2c7c", "Current USD price of debt asset j"],
          ]}
        />
        <P>
          Aave computes one HF per wallet &mdash; all collateral and debt are pooled. Each collateral asset
          carries its own liquidation threshold (LT), so the numerator is not a simple sum but a
          weighted sum. The debt side has no risk weighting. When HF &lt; 1.0, the position is
          eligible for liquidation.
        </P>
        <P>
          The <strong>weighted average HF</strong> shown on the dashboard is the collateral-weighted
          mean across all positions, capped at 20 per position to prevent ultra-safe positions
          (HF = 100,000) from distorting the average.
        </P>
        <Example title="Multi-collateral position">
          {"Collateral:\n  10 WETH  \u00d7 $2,100 \u00d7 0.83 = $17,430\n  0.5 WBTC \u00d7 $68,000 \u00d7 0.73 = $24,820\n  5,000 USDC \u00d7 $1.00 \u00d7 0.78 =  $3,900\n  Weighted sum: $46,150\n\nDebt:\n  20,000 USDT \u00d7 $1.00 = $20,000\n\nHF = $46,150 / $20,000 = 2.308"}
        </Example>
      </Section>

      <Section id="emode" title="E-Mode (Efficiency Mode)">
        <P>
          Aave V3&rsquo;s Efficiency Mode gives borrowers a higher liquidation threshold when their
          collateral and debt are price-correlated. Users opt in by calling{" "}
          <Code>setUserEMode(categoryId)</Code> on the Pool contract &mdash; one E-Mode setting per wallet.
        </P>
        <Formula>{"Standard (LT = 0.83):  Max leverage \u2248 1/(1 \u2212 0.83) \u2248 5.9\u00d7\nE-Mode   (LT = 0.95):  Max leverage \u2248 1/(1 \u2212 0.95) = 20\u00d7"}</Formula>
        <P>
          There are 43 active E-Mode categories on Aave V3 as of April 2026, with LTs ranging from
          71% to 96%. Category 1 (&ldquo;ETH correlated&rdquo;) at LT = 95% dominates &mdash; it covers billions of
          dollars in wstETH/WETH looping positions.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Selective LT application</h4>
        <P>
          Only collateral assets in the E-Mode category&rsquo;s eligible group receive the enhanced LT.
          Mixed-collateral wallets get per-asset treatment:
        </P>
        <Example title="Wallet in ETH E-Mode (LT = 0.95) with mixed collateral">
          {"10 WETH  \u00d7 $2,100 \u00d7 0.95 = $19,950  \u2190 E-Mode LT (ETH group)\n0.5 WBTC \u00d7 $68,000 \u00d7 0.73 = $24,820  \u2190 Standard LT (BTC \u2260 eligible)"}
        </Example>
        <h4 className="text-sm font-semibold text-text-primary mt-4">How we fetch E-Mode data</h4>
        <P>
          The subgraph has no E-Mode data, so we query the Aave Pool contract directly via Multicall3.
          We batch 500 <Code>getUserEMode(address)</Code> calls per RPC request, covering all ~32K
          wallets in ~65 batches (~20 seconds). Category metadata &mdash; including liquidation thresholds
          and liquidation bonuses &mdash; comes from <Code>getEModeCategoryData(uint8)</Code>. These
          on-chain bonuses feed directly into the LaR computation.
        </P>
      </Section>

      <Section id="car" title="Collateral-at-Risk (CaR)">
        <P>
          CaR answers: <em>&ldquo;If prices crash by X%, how much collateral enters the liquidation process?&rdquo;</em>{" "}
          This is a deterministic stress test, not a statistical model &mdash; given an exact price shock,
          we scan all positions and find which ones break HF = 1.0.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Scenarios</h4>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs text-left border-collapse">
            <thead>
              <tr className="text-text-muted border-b border-surface-600">
                <th className="py-2 pr-3 font-medium">Scenario</th>
                <th className="py-2 pr-3 font-medium">Shocked assets</th>
                <th className="py-2 pr-3 font-medium">Unshocked</th>
                <th className="py-2 font-medium">Drop range</th>
              </tr>
            </thead>
            <tbody className="text-text-secondary">
              {([
                ["ETH Crash", "All ETH-group (WETH, wstETH, weETH, \u2026)", "Stablecoins, BTC", "5\u201350%"],
                ["BTC Crash", "All BTC-group (WBTC, cbBTC, tBTC, \u2026)", "Stablecoins, ETH", "5\u201350%"],
                ["Broad Market", "All non-stablecoin assets", "Stablecoins only", "5\u201350%"],
                ["LST Depeg", "LSTs only (wstETH, weETH, rsETH, \u2026) \u2014 not WETH", "WETH, stablecoins", "1\u201320%"],
                ["Ethena Depeg", "USDe, sUSDe, eUSDe, PT tokens", "USDC/USDT", "1\u201320%"],
              ] as const).map(([name, shocked, unshocked, range]) => (
                <tr key={name} className="border-b border-surface-700/50">
                  <td className="py-2 pr-3 font-medium text-text-primary">{name}</td>
                  <td className="py-2 pr-3">{shocked}</td>
                  <td className="py-2 pr-3">{unshocked}</td>
                  <td className="py-2">{range}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <h4 className="text-sm font-semibold text-text-primary mt-6">Correlated shocks &mdash; the critical design decision</h4>
        <P>
          Both collateral <em>and</em> debt prices are shocked for correlated assets. For perfectly
          correlated pairs (e.g. wstETH collateral / WETH debt), price cancels in the HF formula:
        </P>
        <Formula>{"HF = (C \u00d7 k \u00d7 P \u00d7 LT) / (D \u00d7 P) = (C \u00d7 k \u00d7 LT) / D"}</Formula>
        <P>
          Market crashes create <strong>zero liquidation risk</strong> for perfectly correlated pairs.
          The only risks are depeg (k changes), interest accrual (D grows), and governance (LT changes).
          Shocking only the collateral side overstated CaR by 20&times; in our actual data.
        </P>
        <Example title="Why both sides must be shocked">
          {"Position: 100 wstETH ($2,100, LT=0.95) backing 90 WETH ($2,100)\n\nWRONG \u2014 shock collateral only, ETH \u221220%:\n  HF = (100 \u00d7 1,680 \u00d7 0.95) / (90 \u00d7 2,100) = 0.844 \u2192 LIQUIDATED \u2717\n\nCORRECT \u2014 shock both sides, ETH \u221220%:\n  HF = (100 \u00d7 1,680 \u00d7 0.95) / (90 \u00d7 1,680) = 1.056 \u2192 SAFE \u2713"}
        </Example>
        <h4 className="text-sm font-semibold text-text-primary mt-6">Depeg vs. crash</h4>
        <P>
          In a crash, all correlated assets fall together &mdash; E-Mode positions are protected. In a depeg,
          only the derivative drops while the underlying stays flat, collapsing HF. A 5% LST depeg
          typically triggers 20&times;+ more liquidations than a 5% ETH crash.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-6">What CaR represents</h4>
        <P>
          CaR is the total collateral value (at current, pre-crash prices) of positions that become
          eligible for liquidation. It is an upper bound &mdash; actual liquidation volume depends on
          liquidator behavior, partial liquidation rules, and whether borrowers top up collateral.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-6">Shortcut formula</h4>
        <P>For single-collateral, stablecoin-debt positions:</P>
        <Formula>{"shocked_HF = current_HF \u00d7 (1 \u2212 drop%)\n\nPosition liquidates at \u2212X% if current_HF < 1 / (1 \u2212 X%)\n  At \u22125%:  HF < 1.053\n  At \u221210%: HF < 1.111\n  At \u221220%: HF < 1.250\n  At \u221250%: HF < 2.000"}</Formula>
      </Section>

      <Section id="lar" title="Liquidity-at-Risk (LaR)">
        <P>
          CaR tells you <em>what</em> would be liquidated. LaR tells you whether the
          market can actually <em>absorb</em> those liquidations.
        </P>
        <Formula>{"LaR = CaR \u2212 Available Liquidity (per asset)"}</Formula>
        <P>
          Positive LaR means a liquidity shortfall &mdash; liquidations would be unprofitable
          because DEX slippage exceeds the liquidation bonus, so rational liquidators
          won&rsquo;t execute them. The unliquidated positions become bad debt for the protocol.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">How we measure DEX liquidity</h4>
        <P>
          We query the Odos DEX aggregator with progressively larger trade sizes
          ($100K to $100M) for each of 26 liquidation paths (e.g. wstETH&rarr;WETH, weETH&rarr;WETH,
          USDe&rarr;USDC). Odos routes across Uniswap, Curve, Balancer, and hundreds of
          other liquidity sources. For each trade size, we record the realized slippage
          (1 &minus; outputUSD / inputUSD), building a slippage curve per pair.
        </P>
        <P>
          Liquidity scans run every 3 hours for priority pairs and daily for the full set, with results
          cached between scans. The 26 pairs cover LSTs, ETH, BTC, Ethena, stablecoins, and alt tokens,
          providing depth data for 23 of the 27 assets that appear in CaR.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Close factor</h4>
        <P>
          LaR uses a 50% close factor &mdash; each liquidation call on Aave V3 can repay up to 50%
          of the position&rsquo;s debt (when HF is between 0.95 and 1.0). Liquidation demand =
          CaR &times; 50%. This aligns with the cascade simulation&rsquo;s per-position close factor logic.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">The organic threshold: liquidation bonus</h4>
        <P>
          The liquidation bonus defines a natural profitability threshold. A liquidator
          earns the bonus as profit but pays slippage as cost. When slippage exceeds the
          bonus, the trade is a net loss and no rational liquidator will execute it.
          Available liquidity is the maximum trade size where slippage stays below the bonus.
        </P>
        <P>
          Liquidation bonuses are read on-chain from the Aave Pool contract &mdash; both standard
          per-asset bonuses (via the PoolDataProvider) and E-Mode category bonuses
          (via <Code>getEModeCategoryData</Code>). This eliminates hardcoded defaults for
          all but a few exotic assets (e.g. Pendle PT tokens).
        </P>
        <Example title="E-Mode tightens the constraint dramatically">
          {"Standard position: 5% liquidation bonus\n  \u2192 Profitable up to 5% slippage\n  \u2192 wstETH\u2192WETH: ~$50M available\n\nE-Mode position: 1% liquidation bonus\n  \u2192 Profitable up to only 1% slippage\n  \u2192 wstETH\u2192WETH: ~$10M available\n\nResult: E-Mode has 5x LESS effective liquidity,\ndespite using the same DEX pools."}
        </Example>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Per-asset aggregation (no netting)</h4>
        <P>
          LaR is computed per-asset and summed &mdash; excess liquidity on one asset cannot
          offset a shortfall on another. WETH&rarr;USDC liquidity cannot help sell weETH.
          This is more conservative than netting but reflects the physical reality of
          liquidation execution &mdash; each path is independent.
        </P>
        <Formula>{"\u03a3 max(0, CaR\u1d62 \u2212 Available\u1d62) = Total Shortfall\n\u03a3 min(CaR\u1d62, Available\u1d62) / \u03a3 CaR\u1d62 = Absorbability"}</Formula>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Slippage curves</h4>
        <P>
          The DEX Liquidity Depth chart on the dashboard shows the raw slippage curve for each
          liquidation pair. The horizontal dashed line marks the liquidation bonus threshold.
          The intersection point &mdash; highlighted in yellow &mdash; is the available liquidity:
          the maximum trade size that a rational liquidator would execute.
        </P>
      </Section>


      <Section id="cascade" title="Cascade Simulation">
        <P>
          LaR measures first-order shortfall &mdash; what happens when the initial shock triggers liquidations.
          But those liquidations create selling pressure that moves prices, triggering <em>additional</em>{" "}
          liquidations. The cascade simulation models this feedback loop.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Core loop</h4>
        <Formula>{"Round 0: Apply shock \u2192 compute CaR \u2192 execute absorbable \u2192 record slippage\nRound 1: Slippage from Round 0 \u2192 additional price drop \u2192 new liquidations \u2192 execute \u2192 record\n  ...\nConverge: when new CaR < $100K or no price impact generated"}</Formula>
        <P>
          Price impact is data-driven from the same Odos slippage curves used for LaR &mdash; no free
          parameters. If a round executes $2M of wstETH sales and the slippage curve shows 1% at that
          size, wstETH price drops an additional 1% before the next round.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Close factor</h4>
        <P>
          Aave V3 allows liquidators to repay up to 50% of a position&rsquo;s debt per call when
          HF is between 0.95 and 1.0. When HF drops below 0.95, up to 100% can be liquidated.
          The cascade simulation applies the correct close factor per position per round:
        </P>
        <Formula>{"Close factor = 100%  if shocked_HF < 0.95\nClose factor =  50%  if 0.95 \u2264 shocked_HF < 1.0"}</Formula>
        <P>
          After a 50% liquidation, the position&rsquo;s HF typically recovers above 1.0, preventing
          further liquidation. This is why the cascade close factor matters &mdash; using 100% for all
          positions would overstate liquidation demand by roughly 2&times;.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Depth replenishment modes</h4>
        <P>
          Between cascade rounds, DEX liquidity may or may not recover. We model three scenarios:
        </P>
        <ul className="space-y-1.5 text-sm text-text-secondary leading-relaxed">
          <li>
            <strong className="text-text-primary">Conservative (static depth)</strong> &mdash;
            liquidity consumed in each round is gone permanently. No recovery between waves.
            Best model for rapid cascades that execute within minutes or blocks.
            This is the recommended default for risk decisions.
          </li>
          <li>
            <strong className="text-text-primary">Moderate (partial, 30% recovery)</strong> &mdash;
            30% of consumed depth recovers between rounds, reflecting some arbitrageur
            refills between blocks.
          </li>
          <li>
            <strong className="text-text-primary">Optimistic (full recovery)</strong> &mdash;
            depth fully resets each round. Assumes well-capitalized liquidators and sufficient
            time for order books to refill.
          </li>
        </ul>
        <h4 className="text-sm font-semibold text-text-primary mt-4">The replenishment paradox</h4>
        <P>
          Counter-intuitively, the optimistic (full recovery) mode often produces <em>more</em> total
          bad debt than the conservative mode. With full depth recovery, liquidators keep executing
          every round, generating sustained selling pressure that triggers positions deeper in the
          stack. In static mode, depth depletes after round 1, selling stops, and positions further
          back survive. More liquidity recovery paradoxically amplifies cascade severity &mdash; an
          analogy to fire-sale spirals in traditional finance.
        </P>
        <h4 className="text-sm font-semibold text-text-primary mt-4">Key metrics</h4>
        <Dl
          items={[
            ["1st-Order CaR", "Collateral entering liquidation from the initial shock alone (= standard CaR)"],
            ["Total CaR", "All rounds combined. Total CaR / 1st-Order CaR = cascade multiplier"],
            ["Cascade multiplier", "How much cascade effects amplify the initial shock. 1.5\u00d7 means selling pressure adds 50% more liquidations"],
            ["Potential bad debt", "Total CaR minus what DEX markets can absorb across all rounds. This is the protocol\u2019s maximum loss exposure"],
            ["Cascade rounds", "Number of sell\u2192impact\u2192liquidate waves before convergence"],
          ]}
        />
        <h4 className="text-sm font-semibold text-text-primary mt-6">Liquidator collateral choice</h4>
        <P>
          When a position with mixed collateral (e.g. wstETH + AAVE) is liquidated, the liquidator
          calls <Code>liquidationCall(collateralAsset, ...)</Code> and chooses which collateral to seize.
          Rational liquidators preferentially seize the shocked asset &mdash; it&rsquo;s the one they
          need to sell, and the unshocked collateral has no urgency.
        </P>
        <P>
          The cascade simulation attributes CaR only to directly shocked collateral assets. If a position
          holds wstETH and AAVE and gets liquidated during an LST depeg, only the wstETH portion enters
          the cascade selling calculation. This avoids overstating contagion into unrelated assets.
        </P>
        <P>
          <strong className="text-text-primary">Assumption:</strong> Cross-asset contagion from
          mixed-collateral positions is not yet modeled. In practice, some non-shocked collateral
          may be seized in subsequent liquidation calls after the shocked collateral is exhausted.
          This is a conservative simplification &mdash; the model understates rather than overstates
          contagion risk.
        </P>

        <h4 className="text-sm font-semibold text-text-primary mt-6">Effective price drop</h4>
        <P>
          The initial price shock (e.g. 5% LST depeg) is not the final price impact. Cascade selling
          pushes prices further. The effective price drop per asset is:
        </P>
        <Formula>{"Effective drop = Initial shock + Σ(cascade selling impact per round)"}</Formula>
        <P>
          Assets are classified as <strong className="text-risk-danger">directly shocked</strong> (receive
          both the initial shock and cascade impact) or <strong className="text-risk-warning">cascade
          contagion</strong> (not shocked initially, but affected by forced selling from mixed-collateral
          positions). The effective price drop table on the dashboard shows both categories &mdash; only
          assets with &ge;1% total impact are displayed.
        </P>
        <P>
          This metric is particularly useful for cross-protocol risk analysis: the effective price drop
          is the exogenous shock that perp DEXs, other lending protocols, and market makers would
          experience from an Aave liquidation cascade.
        </P>

        <h4 className="text-sm font-semibold text-text-primary mt-6">Multi-drop coverage</h4>
        <P>
          The cascade simulation runs at every drop level shown on the dashboard &mdash; 1% through 20%
          for depeg scenarios, 5% through 50% for crash scenarios. Each drop level runs independently
          across all three replenishment modes, for a total of 120 simulations per pipeline cycle
          (~2.5 seconds of compute). The drop level selector in the cascade panel lets users explore
          how cascade severity changes with shock magnitude.
        </P>

        <Example title="LST Depeg -5%, Conservative mode">
          {"Round 0 (Initial shock):\n  1,077 positions break HF=1 \u2192 $2.1B CaR\n  DEX absorbs $129M, shortfall $2.0B\n  Selling causes 8.67% additional price impact on wstETH\n\nWave 1:\n  106 new positions break HF=1 \u2192 $1.1B new CaR\n  DEX absorbs $437K (depth mostly depleted)\n  0.95% additional price impact\n\nWave 2: 6 positions, $611K new CaR\nWave 3: 6 positions, $103K new CaR \u2192 converged\n\nResult: 1.53\u00d7 multiplier, $3.1B potential bad debt"}
        </Example>
      </Section>

      <Section id="concentration" title="Concentration (HHI)">
        <P>
          The Herfindahl&ndash;Hirschman Index measures whether risk is distributed across many
          participants or concentrated in whales:
        </P>
        <Formula>{"HHI = \u03a3(s\u1d62\u00b2)   where s\u1d62 = wallet_collateral\u1d62 / total_collateral"}</Formula>
        <P>
          HHI ranges from 1/N (perfectly distributed) to 1.0 (single entity). The dashboard also
          shows Top-10 share &mdash; the fraction of total collateral held by the 10 largest positions.
          High concentration matters because a single whale liquidation can move market prices and
          trigger cascades.
        </P>
      </Section>

      <Section id="utilization" title="Utilization &amp; Interest Rates">
        <Formula>{"Utilization = total_borrowed / total_supplied"}</Formula>
        <P>Aave uses a kinked interest rate model:</P>
        <Formula>{"if U \u2264 U_optimal:  rate = base + (U / U_optimal) \u00d7 slope\u2081\nif U > U_optimal:  rate = base + slope\u2081 + ((U \u2212 U_optimal) / (1 \u2212 U_optimal)) \u00d7 slope\u2082"}</Formula>
        <P>
          Where slope&#x2082; &gt;&gt; slope&#x2081;, creating a sharp rate increase above optimal utilization. This
          mechanism incentivizes repayment when pools are heavily borrowed.
        </P>
        <P>
          Rate model parameters (base rate, slope&#x2081;, slope&#x2082;, optimal utilization) are fetched
          on-chain from each asset&rsquo;s InterestRateStrategy contract via{" "}
          <Code>getInterestRateData(address)</Code>. The rate curves shown on the dashboard
          use these live parameters, not hardcoded defaults.
        </P>
      </Section>

      <Section id="history" title="Historical Tracking">
        <P>
          The dashboard tracks key risk metrics over time with a 30-day rolling window.
          Each pipeline cycle (~every 15 minutes) produces a compact summary point containing
          position counts, average health factor, near-liquidation counts, total supplied/borrowed,
          and per-scenario CaR/shortfall/available liquidity at the -5% drop level.
        </P>
        <P>
          Historical data is stored in Cloudflare R2 and served via the <Code>/history</Code> API
          endpoint. The Historical Trends chart allows switching between metrics to observe how
          risk evolves: Are health factors deteriorating? Is DEX liquidity drying up?
          Did a governance change affect shortfall?
        </P>
      </Section>

      <Section id="validation" title="Validation">
        <P>
          We validated our HF calculations against Aave&rsquo;s on-chain{" "}
          <Code>Pool.getUserAccountData()</Code> for four position types:
        </P>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs text-left border-collapse">
            <thead>
              <tr className="text-text-muted border-b border-surface-600">
                <th className="py-2 pr-3 font-medium">Position type</th>
                <th className="py-2 pr-3 font-medium">Our HF</th>
                <th className="py-2 pr-3 font-medium">Aave HF</th>
                <th className="py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody className="text-text-secondary">
              {([
                ["ETH E-Mode (wstETH+weETH / WETH)", "1.0405", "1.0399", "0.05%"],
                ["Stablecoin E-Mode (USDe / USDT+USDC)", "1.0337", "1.0336", "0.01%"],
                ["BTC mixed / stablecoins", "2.0336", "2.0361", "0.12%"],
                ["ETH / stablecoins (non-E-Mode)", "1.9968", "2.0695", "3.51%"],
              ] as const).map(([type, ours, aave, err]) => (
                <tr key={type} className="border-b border-surface-700/50">
                  <td className="py-2 pr-3 font-medium text-text-primary">{type}</td>
                  <td className="py-2 pr-3 font-mono">{ours}</td>
                  <td className="py-2 pr-3 font-mono">{aave}</td>
                  <td className="py-2 font-mono">{err}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <P>
          The worst-case 3.51% error comes from subgraph lag in non-E-Mode positions &mdash;
          the subgraph occasionally reflects prices a few minutes behind the chain.
        </P>
      </Section>

      <Section id="limitations" title="Limitations">
        <ul className="space-y-2 text-sm text-text-secondary leading-relaxed">
          {[
            ["No probability distributions", "This is a deterministic stress test, not VaR. We don\u2019t model return distributions \u2014 crypto is non-stationary with frequent regime changes."],
            
            ["Liquidation dynamics simplified", "The cascade simulation models sequential rounds with per-position close factors (50%/100%), but within each round, all liquidations are treated as simultaneous. Real cascades involve block-by-block competition among MEV searchers."],
            ["Liquidator collateral choice simplified", "The cascade assumes liquidators always seize the shocked collateral asset. In practice, liquidators choose the most profitable collateral to seize, which may not always be the shocked asset. This means cross-asset contagion from mixed-collateral positions is understated."],
            ["Single-factor shocks", "Each scenario shocks one factor (e.g. ETH price). No basis widening, gas spikes, or interest rate changes within a scenario."],
            ["DEX liquidity is a snapshot", "Depth curves are refreshed every 3 hours via Odos. Actual liquidity during a crisis could differ significantly \u2014 LPs may withdraw, arbitrageurs may consume depth, or new liquidity may appear."],
            ["No oracle / governance / smart contract risk", "We trust reported prices and protocol parameters as ground truth."],
            ["Subgraph staleness", "Prices can lag by a few minutes, causing up to ~3.5% HF error for non-E-Mode positions."],
            ["Some assets without DEX depth data", "Pendle PT tokens and a few exotic assets are not routed by Odos and show zero available liquidity (flagged with \u26a0 in the LaR table)."],
          ].map(([title, desc]) => (
            <li key={title}>
              <strong className="text-text-primary">{title}.</strong> {desc}
            </li>
          ))}
        </ul>
      </Section>

      <Section id="pipeline" title="Data Pipeline">
        <P>
          The pipeline runs 24/7 on a dedicated machine, alternating between full scans and delta
          updates for both Aave V3 and Compound V3:
        </P>
        <ul className="space-y-1.5 text-sm text-text-secondary leading-relaxed">
          <li>
            <strong className="text-text-primary">Full scan</strong> (~200s, every ~1.5 hours):
            3-pass architecture &mdash; (1) discover all borrower positions via The Graph subgraph,
            (2) match collateral positions, (3) batch-query E-Mode categories via Multicall3 RPC.
            Also fetches on-chain interest rate parameters and liquidation bonuses.
          </li>
          <li>
            <strong className="text-text-primary">Delta scan</strong> (~21s, every 15 min):
            query recent on-chain events (deposits, borrows, repays, withdrawals) to find changed
            wallets, re-fetch only those positions, merge into cached state. Both Aave V3 and
            Compound V3 support delta updates &mdash; 95% fewer queries than a full scan.
          </li>
          <li>
            <strong className="text-text-primary">Liquidity scan</strong> (~60s, every 3 hours):
            query Odos DEX aggregator for slippage curves across 26 liquidation pairs at 8 trade sizes.
            Results are cached and used for LaR computation on every cycle.
          </li>
          <li>
            <strong className="text-text-primary">Historical tracking</strong> (every cycle):
            extract compact summary point (~800 bytes) and append to a 30-day rolling history.
          </li>
        </ul>
        <P>
          Data flows through: The Graph &rarr; Python pipeline &rarr; Cloudflare R2 &rarr; Cloudflare Worker &rarr; React SPA.
          Infrastructure runs on The Graph, Cloudflare R2, Workers, and Pages. All
          components use managed cloud services with minimal operational overhead.
        </P>
      </Section>

      <Section id="contact" title="Contact">
        <P>
          Built by Otar Sepper ({" "}
          <a href="https://sepperlabs.com" className="text-accent hover:underline" target="_blank" rel="noopener noreferrer">
            SepperLabs
          </a>
          ).
          For questions, feedback, or collaboration, reach out at{" "}
          <a href="mailto:feedback@sepperlabs.com" className="text-accent hover:underline">
            feedback@sepperlabs.com
          </a>.
        </P>
      </Section>
    </main>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="space-y-3">
      <h2 className="text-lg font-semibold text-text-primary border-b border-surface-600 pb-2">{title}</h2>
      {children}
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-text-secondary leading-relaxed">{children}</p>;
}

function Formula({ children }: { children: string }) {
  return (
    <pre className="bg-surface-800 text-text-primary text-xs font-mono p-3 rounded-lg overflow-x-auto whitespace-pre leading-relaxed">
      {children}
    </pre>
  );
}

function Code({ children }: { children: string }) {
  return <code className="bg-surface-800 text-accent text-xs font-mono px-1.5 py-0.5 rounded">{children}</code>;
}

function Example({ title, children }: { title: string; children: string }) {
  return (
    <div className="bg-surface-800/60 border border-surface-700 rounded-lg p-3 mt-2">
      <div className="text-xs font-medium text-text-muted mb-1.5">{title}</div>
      <pre className="text-xs font-mono text-text-secondary whitespace-pre overflow-x-auto leading-relaxed">{children}</pre>
    </div>
  );
}

function Dl({ items }: { items: [string, string][] }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs mt-1">
      {items.map(([term, def]) => (
        <div key={term} className="contents">
          <dt className="font-mono text-accent font-medium">{term}</dt>
          <dd className="text-text-secondary">{def}</dd>
        </div>
      ))}
    </div>
  );
}
